import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../contexts/ToastContext';
import DeleteConfirmModal from '../components/admin/DeleteConfirmModal';
import './ArtistSales.css';

interface Sale {
  id: number;
  name: string;
  description?: string;
  saleType: string; // "Artist" only
  artistId?: number;
  discountType: string; // "Percentage" or "FixedAmount"
  discountValue: number;
  startDate: string;
  endDate?: string;
  compoundWithArtistDiscounts: boolean;
  isActive: boolean;
  isArchived: boolean;
  showBadge: boolean;
  badgeText?: string;
  badgeColor?: string;
  badgeBackgroundColor?: string;
  createdAt: string;
  updatedAt?: string;
}

interface CreateSaleDto {
  name: string;
  description?: string;
  saleType: string;
  discountType: string;
  discountValue: number;
  startDate: string;
  endDate?: string;
  isActive: boolean;
  showBadge: boolean;
  badgeText?: string;
  badgeColor?: string;
  badgeBackgroundColor?: string;
}

const ArtistSales: React.FC = () => {
  const { success, error: showError } = useToast();
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingSale, setEditingSale] = useState<Sale | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; id: number | null; name?: string }>({ show: false, id: null });
  const [deleting, setDeleting] = useState<boolean>(false);
  const [activeOnly, setActiveOnly] = useState<boolean>(false);
  const [formData, setFormData] = useState<CreateSaleDto>({
    name: '',
    description: '',
    saleType: 'Artist',
    discountType: 'Percentage',
    discountValue: 0,
    startDate: new Date().toISOString().slice(0, 16),
    endDate: undefined,
    isActive: true,
    showBadge: true,
    badgeText: 'SALE',
    badgeColor: '#ffffff',
    badgeBackgroundColor: '#ff4444'
  });

  const fetchSales = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<Sale[]>('/artist/sales', {
        params: { activeOnly: activeOnly || undefined }
      });
      setSales(response.data);
    } catch (error) {
      console.error('Failed to fetch sales:', error);
      showError('Failed to fetch sales');
    } finally {
      setLoading(false);
    }
  }, [activeOnly, showError]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      if (!formData.name.trim()) {
        showError('Sale name is required');
        return;
      }
      if (formData.discountValue <= 0) {
        showError('Discount value must be greater than 0');
        return;
      }
      if (formData.discountType === 'Percentage' && formData.discountValue > 100) {
        showError('Discount percentage cannot exceed 100%');
        return;
      }
      if (formData.endDate && formData.startDate >= formData.endDate) {
        showError('Start date must be before end date');
        return;
      }

      const submitData: any = {
        ...formData,
        saleType: 'Artist', // Always Artist for artist sales
        startDate: new Date(formData.startDate).toISOString(),
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null
      };

      if (editingSale) {
        await api.put(`/artist/sales/${editingSale.id}`, submitData);
        success('Sale updated');
      } else {
        await api.post('/artist/sales', submitData);
        success('Sale created');
      }

      setShowForm(false);
      setEditingSale(null);
      resetForm();
      fetchSales();
    } catch (error: any) {
      console.error('Failed to save sale:', error);
      showError(error.response?.data?.message || 'Failed to save sale');
    }
  };

  const handleEdit = (sale: Sale): void => {
    setEditingSale(sale);
    setFormData({
      name: sale.name,
      description: sale.description || '',
      saleType: 'Artist',
      discountType: sale.discountType,
      discountValue: sale.discountValue,
      startDate: sale.startDate.slice(0, 16),
      endDate: sale.endDate ? sale.endDate.slice(0, 16) : undefined,
      isActive: sale.isActive,
      showBadge: sale.showBadge,
      badgeText: sale.badgeText || 'SALE',
      badgeColor: sale.badgeColor || '#ffffff',
      badgeBackgroundColor: sale.badgeBackgroundColor || '#ff4444'
    });
    setShowForm(true);
  };

  const handleDelete = (saleId: number): void => {
    const sale = sales.find(s => s.id === saleId);
    setDeleteConfirm({ show: true, id: saleId, name: sale?.name });
  };

  const confirmDelete = async (): Promise<void> => {
    if (!deleteConfirm.id) return;
    try {
      setDeleting(true);
      await api.delete(`/artist/sales/${deleteConfirm.id}`);
      success('Sale deleted');
      fetchSales();
      setDeleteConfirm({ show: false, id: null });
    } catch (error) {
      showError('Failed to delete sale');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (sale: Sale): Promise<void> => {
    try {
      await api.put(`/artist/sales/${sale.id}`, { isActive: !sale.isActive });
      success(sale.isActive ? 'Sale deactivated' : 'Sale activated');
      fetchSales();
    } catch (error) {
      showError('Failed to update sale');
    }
  };

  const resetForm = (): void => {
    setFormData({
      name: '',
      description: '',
      saleType: 'Artist',
      discountType: 'Percentage',
      discountValue: 0,
      startDate: new Date().toISOString().slice(0, 16),
      endDate: undefined,
      isActive: true,
      showBadge: true,
      badgeText: 'SALE',
      badgeColor: '#ffffff',
      badgeBackgroundColor: '#ff4444'
    });
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  const isCurrentlyActive = (sale: Sale): boolean => {
    const now = new Date();
    const start = new Date(sale.startDate);
    const end = sale.endDate ? new Date(sale.endDate) : null;
    return sale.isActive && now >= start && (!end || now <= end);
  };

  if (loading) {
    return <div className="loading">Loading sales...</div>;
  }

  return (
    <div className="artist-sales">
      <div className="container">
        <div className="page-header">
          <div className="page-header-left">
            <Link to="/profile" className="back-button">
              <i className="fas fa-arrow-left"></i> Back to Profile
            </Link>
            <h1 className="page-title">My Sales</h1>
          </div>
          <div className="page-header-actions">
            <label className="filter-toggle">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
              Active Only
            </label>
            <button
              onClick={() => {
                resetForm();
                setEditingSale(null);
                setShowForm(true);
              }}
              className="btn btn-primary"
            >
              <i className="fas fa-plus"></i> Create Sale
            </button>
          </div>
        </div>

        {showForm && (
          <div className="sale-form-container">
            <form onSubmit={handleSubmit}>
              <h2>{editingSale ? 'Edit Sale' : 'Create Sale'}</h2>
              
              <div className="form-group">
                <label htmlFor="name">Sale Name *</label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="discountType">Discount Type *</label>
                <select
                  id="discountType"
                  value={formData.discountType}
                  onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                  required
                >
                  <option value="Percentage">Percentage (%)</option>
                  <option value="FixedAmount">Fixed Amount ($)</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="discountValue">
                  Discount Value * {formData.discountType === 'Percentage' ? '(0-100)' : '($)'}
                </label>
                <input
                  id="discountValue"
                  type="number"
                  step={formData.discountType === 'Percentage' ? '0.01' : '0.01'}
                  min="0"
                  max={formData.discountType === 'Percentage' ? '100' : undefined}
                  value={formData.discountValue}
                  onChange={(e) => setFormData({ ...formData, discountValue: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="startDate">Start Date *</label>
                  <input
                    id="startDate"
                    type="datetime-local"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="endDate">End Date (Optional)</label>
                  <input
                    id="endDate"
                    type="datetime-local"
                    value={formData.endDate || ''}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value || undefined })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  Active
                </label>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.showBadge}
                    onChange={(e) => setFormData({ ...formData, showBadge: e.target.checked })}
                  />
                  Show Badge
                </label>
              </div>

              {formData.showBadge && (
                <>
                  <div className="form-group">
                    <label htmlFor="badgeText">Badge Text</label>
                    <input
                      id="badgeText"
                      type="text"
                      value={formData.badgeText || ''}
                      onChange={(e) => setFormData({ ...formData, badgeText: e.target.value })}
                      placeholder="SALE"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="badgeColor">Badge Text Color</label>
                      <input
                        id="badgeColor"
                        type="color"
                        value={formData.badgeColor || '#ffffff'}
                        onChange={(e) => setFormData({ ...formData, badgeColor: e.target.value })}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="badgeBackgroundColor">Badge Background Color</label>
                      <input
                        id="badgeBackgroundColor"
                        type="color"
                        value={formData.badgeBackgroundColor || '#ff4444'}
                        onChange={(e) => setFormData({ ...formData, badgeBackgroundColor: e.target.value })}
                      />
                    </div>
                  </div>

                  {formData.badgeText && (
                    <div className="badge-preview">
                      <div
                        className="preview-badge"
                        style={{
                          backgroundColor: formData.badgeBackgroundColor || '#ff4444',
                          color: formData.badgeColor || '#ffffff'
                        }}
                      >
                        {formData.badgeText}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingSale ? 'Update Sale' : 'Create Sale'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingSale(null);
                    resetForm();
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <DeleteConfirmModal
          isOpen={deleteConfirm.show}
          onClose={() => setDeleteConfirm({ show: false, id: null })}
          onConfirm={confirmDelete}
          loading={deleting}
          itemName={deleteConfirm.name}
        />

        <div className="sales-list">
          {sales.length === 0 ? (
            <div className="empty-state">
              <p>No sales found. Create your first sale to promote your artwork!</p>
            </div>
          ) : (
            <table className="sales-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Discount</th>
                  <th>Dates</th>
                  <th>Status</th>
                  <th>Badge</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((sale) => (
                  <tr key={sale.id} className={isCurrentlyActive(sale) ? 'active-sale' : ''}>
                    <td>
                      <strong>{sale.name}</strong>
                      {sale.description && (
                        <div className="sale-description">{sale.description}</div>
                      )}
                    </td>
                    <td>
                      {sale.discountType === 'Percentage' ? (
                        <strong>{sale.discountValue}%</strong>
                      ) : (
                        <strong>${sale.discountValue.toFixed(2)}</strong>
                      )}
                    </td>
                    <td>
                      <div>Start: {formatDate(sale.startDate)}</div>
                      {sale.endDate && <div>End: {formatDate(sale.endDate)}</div>}
                      {isCurrentlyActive(sale) && (
                        <span className="status-badge active">Active Now</span>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${sale.isActive ? 'active' : 'inactive'}`}>
                        {sale.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {sale.showBadge && sale.badgeText ? (
                        <div
                          className="sale-badge-preview"
                          style={{
                            backgroundColor: sale.badgeBackgroundColor || '#ff4444',
                            color: sale.badgeColor || '#ffffff'
                          }}
                        >
                          {sale.badgeText}
                        </div>
                      ) : (
                        <span className="text-muted">No badge</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEdit(sale)}
                          className="btn-icon"
                          title="Edit sale"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          onClick={() => handleToggleActive(sale)}
                          className={`btn-icon ${sale.isActive ? 'btn-icon-warning' : 'btn-icon-success'}`}
                          title={sale.isActive ? 'Deactivate' : 'Activate'}
                        >
                          <i className={`fas fa-${sale.isActive ? 'pause' : 'play'}`}></i>
                        </button>
                        <button
                          onClick={() => handleDelete(sale.id)}
                          className="btn-icon btn-icon-danger"
                          title="Delete sale"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArtistSales;
